import { StyleSheet } from 'react-native';
import { createThemedStyles } from '../theme/useThemedStyles';

// Helper function to safely combine styles with validation errors
export const getInputStyle = (baseStyle: any, hasError: boolean, errorStyle: any) => {
  return hasError ? [baseStyle, errorStyle] : baseStyle;
};

// Helper function to check if a validation error exists
export const hasValidationError = (error: string | undefined | null): boolean => {
  return error !== undefined && error !== null && error !== '';
};

// Helper function to safely combine styles with validation errors
export const getStyleWithError = (baseStyle: any, error: string | undefined | null, errorStyle: any) => {
  return hasValidationError(error) ? [baseStyle, errorStyle] : baseStyle;
};

/** Shared settings/form styles — rebuilds when theme changes. */
export const useSharedStyles = createThemedStyles((colors) => ({
  container: {
    flex: 1,
    backgroundColor: colors.background.default,
    padding: 16,
  },
  inputContainer: {
    marginBottom: 24,
  },
  inputGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: colors.text.primary,
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: colors.surface,
    color: colors.text.primary,
  },
  button: {
    backgroundColor: colors.button.primary,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginVertical: 8,
  },
  buttonText: {
    color: colors.button.primaryText,
    fontSize: 16,
    fontWeight: '600',
  },
  logoutButton: {
    backgroundColor: colors.error.main,
  },
  successMessage: {
    color: colors.success.main,
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
    marginTop: 8,
  },
  pickerContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  pickerOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  pickerOptionSelected: {
    backgroundColor: colors.button.primary,
    borderColor: colors.button.primary,
  },
  pickerOptionText: {
    fontSize: 14,
    color: colors.text.primary,
  },
  switchContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  subscriptionInfo: {
    backgroundColor: colors.surface,
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  subscriptionTier: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.button.primary,
    marginBottom: 4,
  },
  subscriptionStatus: {
    fontSize: 14,
    color: colors.text.secondary,
    marginBottom: 4,
  },
  subscriptionDate: {
    fontSize: 12,
    color: colors.text.secondary,
  },
  upgradeButton: {
    backgroundColor: colors.button.primary,
  },
  upgradeContainer: {
    backgroundColor: colors.warning.background,
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
  },
  upgradeText: {
    color: colors.warning.dark,
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 12,
  },
  upgradeButtonText: {
    color: colors.text.onDark,
    fontSize: 14,
    fontWeight: '600',
  },
  supportText: {
    color: colors.text.secondary,
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
  },
  testingAccountLabel: {
    backgroundColor: colors.warning.background,
    color: colors.warning.dark,
    padding: 8,
    borderRadius: 4,
    fontSize: 12,
    marginBottom: 16,
  },
  developerNote: {
    backgroundColor: colors.info.background,
    color: colors.info.dark,
    padding: 12,
    borderRadius: 8,
    fontSize: 12,
    marginTop: 12,
    lineHeight: 18,
  },
  versionContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  versionText: {
    fontSize: 14,
    color: colors.text.secondary,
  },
  deleteAccountContainer: {
    backgroundColor: colors.error.background,
    padding: 16,
    borderRadius: 8,
  },
  deleteAccountWarning: {
    color: colors.error.dark,
    fontSize: 14,
    marginBottom: 16,
    lineHeight: 20,
  },
  deleteAccountButton: {
    backgroundColor: colors.error.main,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  deleteAccountButtonText: {
    color: colors.text.onDark,
    fontSize: 16,
    fontWeight: '600',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 400,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: colors.text.primary,
    marginBottom: 16,
    textAlign: 'center',
  },
  modalWarning: {
    color: colors.error.main,
    fontSize: 14,
    marginBottom: 16,
    textAlign: 'center',
    lineHeight: 20,
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 16,
  },
  modalButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButton: {
    backgroundColor: colors.text.secondary,
  },
  saveButton: {
    backgroundColor: colors.button.primary,
  },
  deleteButton: {
    backgroundColor: colors.error.main,
  },
  modalButtonText: {
    color: colors.text.onDark,
    fontSize: 16,
    fontWeight: '600',
  },
  modalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContentContainer: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 24,
    width: '90%',
    maxWidth: 300,
  },
  modalButtonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
    marginTop: 16,
  },
  successText: {
    color: colors.success.main,
    fontSize: 14,
    fontWeight: '500',
    textAlign: 'center',
    marginTop: 16,
  },
}));
